/**
 * 千里江山圖風格地形渲染
 *
 * 靈感來自王希孟《千里江山圖》的青綠山水風格：
 * - 石青（azurite blue）：山頂與遠山
 * - 石綠（malachite green）：山腰與林地
 * - 赭石（ochre）：山腳與土壤
 * - 宣紙底色：低處與平地
 * - 墨線描邊：山脊與坡度變化處
 * - 金色雲霧：高處大氣
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  positionLocal,
  positionWorld,
  normalWorld,
  cameraPosition,
  texture,
  uniform,
  uv,
  smoothstep,
  mix,
  clamp,
  normalize,
  pow,
  fract,
  sin,
  cos,
  dot,
  floor,
  abs,
  max,
  min,
  time as tslTime,
} from 'three/tsl'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  WATER_HEIGHT_RATIO,
  WATER_LEVEL,
  HEIGHTMAP_SIZE,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

const SEGMENTS = 512

// ── TSL 共有噪聲函數（供地形與水面共同使用） ──
const hash21 = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const h = dot(p, vec2(127.1, 311.7))
  return fract(sin(h).mul(float(43758.5453)))
})

const valueNoise = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const i = floor(p)
  const f = fract(p)
  const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
  const a = hash21(i)
  const b = hash21(i.add(vec2(1, 0)))
  const c = hash21(i.add(vec2(0, 1)))
  const d = hash21(i.add(vec2(1, 1)))
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
})

const fbm = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const v = float(0).toVar()
  const a = float(0.5).toVar()
  const shift = vec2(100.0, 100.0)
  v.addAssign(a.mul(valueNoise(p)))
  p.assign(p.mul(float(2.0)).add(shift))
  a.mulAssign(float(0.5))
  v.addAssign(a.mul(valueNoise(p)))
  p.assign(p.mul(float(2.0)).add(shift))
  a.mulAssign(float(0.5))
  v.addAssign(a.mul(valueNoise(p)))
  p.assign(p.mul(float(2.0)).add(shift))
  a.mulAssign(float(0.5))
  v.addAssign(a.mul(valueNoise(p)))
  return v
})

export function TerrainMeshQingLv() {
  const meshRef = useRef<THREE.Mesh>(null)
  const lastHmapVersion = useRef(0)

  const { heightTex, material } = useMemo(() => {
    const tex = new THREE.DataTexture(
      terrainBuffer.heightmap,
      HEIGHTMAP_SIZE,
      HEIGHTMAP_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType,
    )
    tex.needsUpdate = true
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter

    const heightScale = uniform(TERRAIN_HEIGHT_SCALE)
    const texelSize = uniform(1.0 / HEIGHTMAP_SIZE)

    const mat = new THREE.MeshStandardNodeMaterial()
    mat.side = THREE.DoubleSide
    mat.flatShading = false
    mat.transparent = true
    mat.depthWrite = true

    // ── 頂點位移（3×3 高斯平滑，2-texel 半徑 — 更大範圍柔化山脊/山頂） ──
    mat.positionNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const texUV = uv()
      const eps = texelSize.mul(float(2)) // 2 texel 半徑覆蓋 5×5 等效區域
      // 取 3×3 鄰域（間距 2 texels）
      const h00 = texture(tex, texUV.add(vec2(eps.negate(), eps.negate()))).x
      const h10 = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x
      const h20 = texture(tex, texUV.add(vec2(eps, eps.negate()))).x
      const h01 = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const h11 = texture(tex, texUV).x
      const h21 = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const h02 = texture(tex, texUV.add(vec2(eps.negate(), eps))).x
      const h12 = texture(tex, texUV.add(vec2(float(0), eps))).x
      const h22 = texture(tex, texUV.add(vec2(eps, eps))).x
      // 高斯核 [1 2 1; 2 4 2; 1 2 1] / 16
      const smoothH = h00
        .add(h10.mul(float(2))).add(h20)
        .add(h01.mul(float(2))).add(h11.mul(float(4))).add(h21.mul(float(2)))
        .add(h02).add(h12.mul(float(2))).add(h22)
        .div(float(16))
      const h = smoothH.mul(heightScale)
      return vec3(localPos.x, localPos.y, h)
    })()

    // ── 法線（3-texel 取樣距離，與頂點平滑配合） ──

    // ──《千里江山圖》青綠山水著色 ──
    mat.colorNode = Fn(() => {
      const texUV = uv()
      const eps = texelSize
      const sample = texture(tex, texUV)
      const normH = sample.x

      // 坡度
      const hR = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const hL = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const hU = texture(tex, texUV.add(vec2(float(0), eps))).x
      const hD = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x
      const worldStep = float(TERRAIN_WORLD_SIZE).div(float(HEIGHTMAP_SIZE))
      const dhdx = hR.sub(hL).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const dhdy = hU.sub(hD).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const slopeLen = pow(dhdx.mul(dhdx).add(dhdy.mul(dhdy)), float(0.5))
      const normalY = float(1).div(pow(float(1).add(slopeLen.mul(slopeLen)), float(0.5)))

      // noise 打破色帶
      const noiseCoord = texUV.mul(float(HEIGHTMAP_SIZE))
      const brushNoise = fbm(noiseCoord.mul(float(0.3)))
      const fineNoise = valueNoise(noiseCoord.mul(float(1.5)))
      const breakOffset = brushNoise.sub(float(0.5)).mul(float(0.02))
      const nH = normH.add(breakOffset)
      const waterH = float(WATER_HEIGHT_RATIO)

      // ──────── 千里江山圖色彩 ────────
      // 宣紙底色（暖米色）
      const paperBase = vec3(0.92, 0.88, 0.78)

      // 沙灘/水邊（赭石偏暖）
      const shoreSand = vec3(0.82, 0.72, 0.52)

      // 石綠（malachite green）— 山腰主色
      const malachite1 = vec3(0.18, 0.52, 0.32)    // 深石綠
      const malachite2 = vec3(0.30, 0.62, 0.38)    // 淺石綠

      // 石青（azurite blue）— 山頂高處
      const azurite1 = vec3(0.15, 0.32, 0.55)      // 深石青
      const azurite2 = vec3(0.22, 0.42, 0.62)      // 淺石青

      // 赭石（ochre）— 崖面/土壤
      const ochre = vec3(0.65, 0.45, 0.25)

      // 雲母白/雪（高處）
      const cloudWhite = vec3(0.95, 0.93, 0.88)

      // ── 高度色帶（模擬礦物顏料層層疊加） ──
      // 水下 → 紙底
      let color: any = paperBase
      // 沙灘
      color = mix(color, shoreSand, smoothstep(waterH.sub(float(0.01)), waterH.add(float(0.008)), nH))
      // 低坡: 石綠淺
      color = mix(color, malachite2, smoothstep(waterH.add(float(0.005)), float(0.49), nH))
      // 中坡: 石綠深（噪聲混合）
      const greenMix = mix(malachite1, malachite2, fineNoise)
      color = mix(color, greenMix, smoothstep(float(0.49), float(0.52), nH))
      // 高處: 石青（降低閾值，讓藍色更早出現、更濃）
      const blueMix = mix(azurite1, azurite2, brushNoise)
      color = mix(color, blueMix, smoothstep(float(0.50), float(0.56), nH))

      // 極高處: 石青加強（越高越濃，避免被崖面赭石完全蓋掉）
      const deepBlueMix = mix(azurite1, vec3(0.18, 0.38, 0.60), fineNoise)
      color = mix(color, deepBlueMix, smoothstep(float(0.58), float(0.68), nH).mul(float(0.6)))
      // 最高: 雲母白（若有雪線）
      const snowLine = float(0.75).add(brushNoise.sub(float(0.5)).mul(float(0.08)))
      color = mix(color, cloudWhite, smoothstep(snowLine, snowLine.add(float(0.05)), normH))

      // ── 山脊石青（曲率驅動：凸面山脊額外疊加藍色） ──
      const hC = sample.x
      const laplacian = hR.add(hL).add(hU).add(hD).sub(hC.mul(float(4)))
      const ridgeFactor = clamp(laplacian.negate().mul(float(50)), float(0), float(1))
      const aboveWater = smoothstep(waterH, waterH.add(float(0.01)), normH)
      // 山脊上疊加石青，不受高度限制
      const ridgeBlue = mix(azurite1, azurite2, fineNoise)
      color = mix(color, ridgeBlue, ridgeFactor.mul(aboveWater).mul(float(0.50)))

      // ── 高處平頂石青補強（normalY 接近 1 的高海拔平面） ──
      const flatTopFactor = smoothstep(float(0.90), float(0.98), normalY)
        .mul(smoothstep(float(0.54), float(0.62), nH))
      const topBlueMix = mix(azurite2, azurite1, brushNoise)
      color = mix(color, topBlueMix, flatTopFactor.mul(aboveWater).mul(float(0.35)))

      // ── 坡度影響：陡峭處著色（高度決定崖面色調） ──
      const cliffBlend = float(1).sub(smoothstep(float(0.55), float(0.82), normalY))

      // 崖面顏色隨高度漸變：低處赭石，高處石青石綠交織
      // 高度因子：0.49 以下全赭石，0.49-0.58 過渡，0.58 以上偏青綠
      const cliffHeightFactor = smoothstep(float(0.49), float(0.58), nH)
      // 崖面石綠（帶噪聲斑駁，模擬礦物質沉積）
      const cliffGreen = mix(malachite1, vec3(0.22, 0.48, 0.35), fineNoise)
      // 崖面石青（深色調，陰面感）
      const cliffAzurite = mix(azurite1, vec3(0.12, 0.28, 0.48), brushNoise)
      // 高處崖面色：石綠石青混合，噪聲決定偏綠還是偏青
      const cliffHighColor = mix(cliffGreen, cliffAzurite, smoothstep(float(0.4), float(0.6), brushNoise))
      // 最終崖面色：赭石-青綠漸變
      const cliffColor = mix(ochre, cliffHighColor, cliffHeightFactor.mul(float(0.75)))

      // 降低崖面覆蓋強度（從 0.7 降至 0.55），讓底層高度色帶透出
      color = mix(color, cliffColor, cliffBlend.mul(aboveWater).mul(float(0.55)))

      // ── 墨線描邊（Laplacian 邊緣偵測 → 深墨色） ──
      const edgeStrength = clamp(abs(laplacian).mul(float(35)), float(0), float(1))
      // 坡度變化處也加墨線
      const slopeEdge = clamp(slopeLen.mul(float(0.15)), float(0), float(1))
      const inkEdge = max(edgeStrength, slopeEdge.mul(float(0.5)))
      const inkColor = vec3(0.08, 0.06, 0.04) // 墨色
      color = mix(color, inkColor, inkEdge.mul(aboveWater).mul(float(0.45)))

      // ── 侵蝕 AO（凹處暗色） ──
      const aoFactor = clamp(float(1).sub(laplacian.mul(float(10))), float(0.5), float(1.15))
      color = mix(color, color.mul(aoFactor), aboveWater)

      // ── 筆觸質感（微 noise 模擬宣紙紋理） ──
      const paperTexture = valueNoise(noiseCoord.mul(float(4.0))).sub(float(0.5)).mul(float(0.04))
      const brushStroke = valueNoise(noiseCoord.mul(float(0.8)).add(vec2(normH.mul(float(50)), float(0)))).sub(float(0.5)).mul(float(0.03))
      color = color.add(vec3(paperTexture.add(brushStroke)).mul(aboveWater))

      return vec4(color, float(1.0))
    })()

    // ── 水面以下漸變透明 ──
    mat.opacityNode = Fn(() => {
      const texUV = uv()
      const normH = texture(tex, texUV).x
      const waterH = float(WATER_HEIGHT_RATIO)
      // 預設高度 0.45 必須完全透明，只有突出水面的地形才可見
      return smoothstep(waterH.sub(float(0.002)), waterH.add(float(0.008)), normH)
    })()

    mat.roughnessNode = float(0.92) // 宣紙質感偏磨砂
    mat.metalnessNode = float(0.0)

    return { heightTex: tex, material: mat }
  }, [])

  useFrame(() => {
    if (terrainBuffer.heightmapVersion !== lastHmapVersion.current) {
      lastHmapVersion.current = terrainBuffer.heightmapVersion
      heightTex.image = {
        data: terrainBuffer.heightmap,
        width: HEIGHTMAP_SIZE,
        height: HEIGHTMAP_SIZE,
      }
      heightTex.needsUpdate = true
    }
  })

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE, TERRAIN_WORLD_SIZE, SEGMENTS, SEGMENTS]} />
      <primitive object={material} />
    </mesh>
  )
}

/** 千里江山圖風格水面 —— 墨波位移 + 近岸蛤粉碎白 */
export function WaterPlaneQingLv() {
  const meshRef = useRef<THREE.Mesh>(null)
  const lastWaterHmapVersion = useRef(0)

  const { heightTex, material } = useMemo(() => {
    const tex = new THREE.DataTexture(
      terrainBuffer.heightmap,
      HEIGHTMAP_SIZE,
      HEIGHTMAP_SIZE,
      THREE.RGBAFormat,
      THREE.FloatType,
    )
    tex.needsUpdate = true
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter

    const mat = new THREE.MeshStandardNodeMaterial()
    mat.transparent = true
    mat.side = THREE.DoubleSide
    mat.depthWrite = false

    const time = tslTime
    const waterLevelU = uniform(WATER_LEVEL)
    const heightScaleU = uniform(TERRAIN_HEIGHT_SCALE)
    const worldSizeU = uniform(TERRAIN_WORLD_SIZE)

    const gerstnerWave = Fn(([pos_immutable, dir_immutable, amplitude_immutable, freq_immutable, speed_immutable]: [any, any, any, any, any]) => {
      const pos = vec2(pos_immutable).toVar()
      const dir = vec2(dir_immutable).toVar()
      const amp = float(amplitude_immutable)
      const frq = float(freq_immutable)
      const spd = float(speed_immutable)
      const phase = dot(dir, pos).mul(frq).add(time.mul(spd))
      const s = sin(phase)
      const c = cos(phase)
      const steepness = float(0.35)
      const dx = steepness.mul(amp).mul(dir.x).mul(c)
      const dz = steepness.mul(amp).mul(dir.y).mul(c)
      const dy = amp.mul(s)
      return vec3(dx, dy, dz)
    })

    const sumWaveHeight = Fn(([p_immutable]: [any]) => {
      const p = vec2(p_immutable).toVar()
      const h1 = gerstnerWave(p, vec2(0.72, 0.28), float(0.55), float(0.045), float(0.32)).y
      const h2 = gerstnerWave(p, vec2(-0.38, 0.62), float(0.28), float(0.1), float(0.55)).y
      const h3 = gerstnerWave(p, vec2(0.25, -0.82), float(0.12), float(0.22), float(0.85)).y
      return h1.add(h2).add(h3)
    })

    mat.positionNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const worldXZ = vec2(localPos.x, localPos.y)
      const w1 = gerstnerWave(worldXZ, vec2(0.72, 0.28), float(0.55), float(0.045), float(0.32))
      const w2 = gerstnerWave(worldXZ, vec2(-0.38, 0.62), float(0.28), float(0.1), float(0.55))
      const w3 = gerstnerWave(worldXZ, vec2(0.25, -0.82), float(0.12), float(0.22), float(0.85))
      const d = w1.add(w2).add(w3)
      return vec3(localPos.x.add(d.x), localPos.y.add(d.z), d.y)
    })()

    mat.normalNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const worldXZ = vec2(localPos.x, localPos.y)
      const eps = float(0.6)
      const hC = sumWaveHeight(worldXZ)
      const hR = sumWaveHeight(worldXZ.add(vec2(eps, float(0))))
      const hU = sumWaveHeight(worldXZ.add(vec2(float(0), eps)))
      return normalize(vec3(hR.sub(hC).div(eps).negate(), hU.sub(hC).div(eps).negate(), float(1)))
    })()

    const computeDepth = Fn(([lx, ly]: [any, any]) => {
      const terrainUV = vec2(
        float(lx).div(worldSizeU).add(float(0.5)),
        float(ly).div(worldSizeU).add(float(0.5)),
      )
      const insideX = smoothstep(float(0.5), float(0.45), abs(terrainUV.x.sub(float(0.5))))
      const insideZ = smoothstep(float(0.5), float(0.45), abs(terrainUV.y.sub(float(0.5))))
      const inside = insideX.mul(insideZ)
      const terrainH = texture(tex, clamp(terrainUV, float(0), float(1))).x.mul(heightScaleU)
      const insideDepth = max(float(0), waterLevelU.sub(terrainH))
      return mix(float(48), insideDepth, inside)
    })

    mat.colorNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const depth = computeDepth(localPos.x, localPos.y)
      const waveH = sumWaveHeight(vec2(localPos.x, localPos.y))

      const waterBase = vec3(0.48, 0.62, 0.60)
      const paperTint = vec3(0.85, 0.82, 0.75)
      const deepTint = vec3(0.32, 0.46, 0.50)
      const baseColor = mix(mix(waterBase, paperTint, float(0.28)), deepTint, smoothstep(float(4), float(28), depth))

      const viewDir = normalize(cameraPosition.sub(positionWorld))
      const N = normalWorld
      const NdotV = max(float(0.001), dot(viewDir, N))
      const fresnel = pow(float(1).sub(NdotV), float(3))
      const skyTint = vec3(0.70, 0.78, 0.82)
      const waterColor = mix(baseColor, skyTint, fresnel.mul(float(0.28)))

      const foamNoise = valueNoise(vec2(localPos.x, localPos.y).mul(float(0.18)).add(vec2(time.mul(0.08), float(0))))
      const shore = float(1).sub(smoothstep(float(0.4), float(7.5), depth))
      const crest = smoothstep(float(0.12), float(0.42), waveH)
      const foam = clamp(shore.mul(float(0.85)).add(crest.mul(shore).mul(foamNoise)), float(0), float(1))
      const foamColor = vec3(0.91, 0.86, 0.78)
      const withFoam = mix(waterColor, foamColor, foam.mul(float(0.72)))

      const fogColor = vec3(0.694, 0.645, 0.527)
      const distFromCenter = pow(
        positionWorld.x.mul(positionWorld.x).add(positionWorld.z.mul(positionWorld.z)),
        float(0.5),
      )
      const edgeFade = smoothstep(float(220), float(520), distFromCenter)
      return vec4(mix(withFoam, fogColor, edgeFade), float(1.0))
    })()

    mat.opacityNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const depth = computeDepth(localPos.x, localPos.y)
      const distFromCenter = pow(
        positionWorld.x.mul(positionWorld.x).add(positionWorld.z.mul(positionWorld.z)),
        float(0.5),
      )
      const edge = mix(float(0.42), float(0.0), smoothstep(float(240), float(560), distFromCenter))
      const shoreAlpha = mix(float(0.22), float(0.5), smoothstep(float(0), float(10), depth))
      return min(edge, shoreAlpha)
    })()
    mat.roughnessNode = float(0.72)
    mat.metalnessNode = float(0.0)

    return { heightTex: tex, material: mat }
  }, [])

  useFrame(() => {
    if (terrainBuffer.heightmapVersion !== lastWaterHmapVersion.current) {
      lastWaterHmapVersion.current = terrainBuffer.heightmapVersion
      heightTex.image = {
        data: terrainBuffer.heightmap,
        width: HEIGHTMAP_SIZE,
        height: HEIGHTMAP_SIZE,
      }
      heightTex.needsUpdate = true
    }
  })

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, WATER_LEVEL, 0]}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE * 3.6, TERRAIN_WORLD_SIZE * 3.6, 160, 160]} />
      <primitive object={material} />
    </mesh>
  )
}
